(() => {
  if (window.productDocumentViewerInitialized) return;
  window.productDocumentViewerInitialized = true;

  const showDocument = (viewer, button) => {
    const image = viewer.querySelector('[data-document-image]');
    const pdf = viewer.querySelector('[data-document-pdf]');
    const isPdf = button.dataset.kind === 'pdf';
    const pagePreviewUrl = button.dataset.pagePreviewUrl;
    const pages = (button.dataset.pagePreviewUrls || pagePreviewUrl || '').split('|').filter(Boolean);
    viewer.documentPages = pages;
    viewer.documentPageIndex = 0;
    viewer.querySelectorAll('[data-document-pages]').forEach(control => {
      control.hidden = pages.length < 2;
      control.querySelector('output').textContent = `Page 1 of ${pages.length}`;
      control.querySelector('[data-document-page="-1"]').disabled = true;
      control.querySelector('[data-document-page="1"]').disabled = pages.length < 2;
    });
    const thumbnailUrl = pagePreviewUrl || button.dataset.thumbnailUrl;
    viewer.querySelector('[data-document-preview]')?.classList.toggle('product-documents__preview--thumbnail', Boolean(thumbnailUrl && !pagePreviewUrl));

    viewer.querySelectorAll('[data-document-view]').forEach((candidate) => {
      const active = candidate === button;
      candidate.classList.toggle('is-active', active);
      candidate.setAttribute('aria-pressed', String(active));
    });
    viewer.querySelectorAll('[data-document-for]').forEach((card) => {
      card.classList.toggle('is-active', card.dataset.documentFor === button.dataset.documentKey);
    });
    viewer.querySelectorAll('[data-document-row]').forEach((row) => {
      row.classList.toggle('is-active', row.contains(button));
    });
    viewer.querySelector('[data-document-preview]')?.style.setProperty('--document-zoom', 1);
    const zoomButton = viewer.querySelector('[data-document-zoom]');
    if (zoomButton) {
      zoomButton.dataset.zoomLevel = '1';
      zoomButton.setAttribute('aria-label', 'Zoom document');
    }

    if (isPdf && !thumbnailUrl) {
      pdf.src = button.dataset.previewUrl;
      pdf.title = button.dataset.previewAlt;
      pdf.hidden = false;
      image.hidden = true;
    } else {
      image.src = thumbnailUrl || button.dataset.previewUrl;
      image.alt = button.dataset.previewAlt;
      image.hidden = false;
      pdf.hidden = true;
      pdf.removeAttribute('src');
    }
  };

  const applyVariantMap = (viewer, map) => {
    if (!map) return;
    const scaleView = viewer.querySelector('[data-document-scale-view]');
    const scaleDownload = viewer.querySelector('[data-document-scale-download]');
    scaleView.dataset.previewUrl = map.dataset.documentScaleUrl;
    scaleView.dataset.previewAlt = map.dataset.documentScaleAlt;
    scaleDownload.href = map.dataset.documentScaleUrl;
    scaleDownload.download = map.dataset.documentScaleFilename;
    const thumbnail = viewer.querySelector('[data-document-scale-thumbnail]');
    if (thumbnail) thumbnail.src = map.dataset.documentScaleUrl;
    if (scaleView.classList.contains('is-active')) showDocument(viewer, scaleView);
  };

  const updateVariantDocuments = (viewer, variant) => {
    const map = [...viewer.querySelectorAll('[data-document-variant]')].find((candidate) => (
      candidate.dataset.documentSku === variant?.sku
      || String(variant?.id || '').endsWith(candidate.dataset.documentVariantId)
    ));
    applyVariantMap(viewer, map);
  };

  const updateOptionDocuments = (viewer, optionValue) => {
    const map = [...viewer.querySelectorAll('[data-document-variant]')]
      .find((candidate) => candidate.dataset.documentOptionValue === optionValue);
    applyVariantMap(viewer, map);
  };

  const initialize = (viewer) => {
    if (viewer.dataset.initialized === 'true') return;
    viewer.dataset.initialized = 'true';
    const pageControls = () => {
      const nav = document.createElement('nav');
      nav.dataset.documentPages = ''; nav.hidden = true;
      nav.setAttribute('aria-label', 'Specification and installation pages');
      nav.style.cssText = 'position:absolute;bottom:8px;left:50%;transform:translateX(-50%);z-index:3;white-space:nowrap;background:white;padding:4px 8px;border:1px solid #ddd;font-size:12px';
      nav.innerHTML = '<button type="button" data-document-page="-1" aria-label="Previous page">‹</button> <output aria-live="polite"></output> <button type="button" data-document-page="1" aria-label="Next page">›</button>';
      return nav;
    };
    viewer.querySelector('[data-document-preview]').append(pageControls());
    viewer.querySelector('[data-document-dialog]').append(pageControls());
    const openDialog = () => {
      const dialog = viewer.querySelector('[data-document-dialog]');
      const active = viewer.querySelector('[data-document-view].is-active');
      const dialogImage = dialog.querySelector('[data-document-dialog-image]');
      const dialogPdf = dialog.querySelector('[data-document-dialog-pdf]');
      const dialogContent = dialog.querySelector('.product-documents__dialog-content');
      const pagePreviewUrl = active?.dataset.pagePreviewUrl;
      const isPdf = active?.dataset.kind === 'pdf' && !pagePreviewUrl;
      dialog.classList.toggle('product-documents__dialog--fit-page', Boolean(pagePreviewUrl));
      dialog.classList.toggle('product-documents__dialog--image', !isPdf);
      dialog.classList.toggle('product-documents__dialog--pdf', isPdf);
      if (isPdf) {
        dialogPdf.src = active.dataset.previewUrl;
        dialogPdf.hidden = false;
        dialogImage.hidden = true;
      } else {
        dialogImage.src = viewer.documentPages?.[viewer.documentPageIndex] || pagePreviewUrl || active.dataset.previewUrl;
        dialogImage.alt = active.dataset.previewAlt;
        dialogImage.hidden = false;
        dialogPdf.hidden = true;
        dialogPdf.removeAttribute('src');
      }
      dialog.showModal();
      if (dialogContent) dialogContent.scrollTop = 0;
    };

    viewer.addEventListener('click', (event) => {
      const pageButton = event.target.closest('[data-document-page]');
      if (pageButton) {
        const pages = viewer.documentPages || [];
        viewer.documentPageIndex = Math.max(0, Math.min(pages.length - 1, viewer.documentPageIndex + Number(pageButton.dataset.documentPage)));
        for (const selector of ['[data-document-image]', '[data-document-dialog-image]']) viewer.querySelector(selector).src = pages[viewer.documentPageIndex];
        viewer.querySelectorAll('[data-document-pages]').forEach(control => {
          control.querySelector('output').textContent = `Page ${viewer.documentPageIndex + 1} of ${pages.length}`;
          control.querySelector('[data-document-page="-1"]').disabled = viewer.documentPageIndex === 0;
          control.querySelector('[data-document-page="1"]').disabled = viewer.documentPageIndex === pages.length - 1;
        });
      }
      const button = event.target.closest('[data-document-view]');
      if (button) {
        showDocument(viewer, button);
      }

      const zoomButton = event.target.closest('[data-document-zoom]');
      if (zoomButton) {
        const preview = viewer.querySelector('[data-document-preview]');
        const current = Number(zoomButton.dataset.zoomLevel || 1);
        const next = current >= 1.75 ? 1 : current + 0.25;
        zoomButton.dataset.zoomLevel = String(next);
        zoomButton.setAttribute('aria-label', next === 1 ? 'Zoom document' : `Document zoom ${Math.round(next * 100)} percent; activate to increase`);
        preview.style.setProperty('--document-zoom', next);
      }

      const expandTrigger = event.target.closest('[data-document-expand-control], [data-document-expand]');
      if (expandTrigger) {
        openDialog();
      }

      if (event.target.closest('[data-document-dialog-close]')) {
        viewer.querySelector('[data-document-dialog]')?.close();
      }
    });

    const previewFromRow = (event) => {
      const row = event.target.closest('[data-document-row]');
      const button = row?.querySelector('[data-document-view]');
      if (button) showDocument(viewer, button);
    };
    viewer.addEventListener('mouseover', previewFromRow);
    viewer.addEventListener('focusin', previewFromRow);

    viewer.querySelector('[data-document-expand]')?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.currentTarget.click();
      }
    });
    viewer.querySelectorAll('[data-document-download]').forEach((link) => {
      link.addEventListener('click', async (event) => {
        event.preventDefault();
        try {
          const response = await fetch(link.href);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const objectUrl = URL.createObjectURL(await response.blob());
          const download = document.createElement('a');
          download.href = objectUrl;
          download.download = link.download;
          download.click();
          setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        } catch (_) {
          window.location.assign(link.href);
        }
      });
    });
  };

  const initializeAll = () => document.querySelectorAll('[data-product-documents]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
  document.addEventListener('change', (event) => {
    if (!event.target.matches('variant-selects input[type="radio"]')) return;
    const viewer = event.target.closest('product-info')?.querySelector('[data-product-documents]');
    if (viewer) updateOptionDocuments(viewer, event.target.value);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const viewer = document.querySelector(`#MainProduct-${data.sectionId} [data-product-documents]`);
      if (viewer) updateVariantDocuments(viewer, data.variant);
    });
  }
})();
